import {Component, OnInit} from '@angular/core';
import {DatePipe} from "@angular/common";
import {HttpClient, HttpParams} from "@angular/common/http";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {AdminReview, REVIEW_SOURCE_LABELS, ReviewSource} from "../../../../models/review";
import {ReviewService} from "../../../../services/review/review.service";
import {StarRatingComponent} from "../../../../components/star-rating/star-rating.component";
import {environment} from "../../../../environments/environment";
import {Page} from "../../../../models/paginator";
import {Product} from "../../../../models/product";

/**
 * Reviews tab of the admin panel: approve what shoppers send, and add comments that came in
 * elsewhere (Facebook, Viber) with their source and original date.
 */
@Component({
    selector: 'app-admin-reviews',
    standalone: true,
    imports: [DatePipe, ReactiveFormsModule, StarRatingComponent],
    templateUrl: './admin-reviews.component.html'
})
export class AdminReviewsComponent implements OnInit {

    reviews: AdminReview[] = [];

    products: Product[] = [];

    /** Review being edited, or null when the form adds a new one. */
    editingId: number | null = null;

    showForm = false;

    saving = false;

    error = '';

    readonly sources: ReviewSource[] = ['FACEBOOK', 'INSTAGRAM', 'VIBER', 'WEBSITE', 'OTHER'];

    readonly sourceLabels: Record<ReviewSource, string> = {...REVIEW_SOURCE_LABELS, WEBSITE: 'од сајтот', OTHER: 'друго'};

    form = new FormGroup({
        productId: new FormControl<number | null>(null),
        authorName: new FormControl('', {nonNullable: true, validators: [Validators.required, Validators.maxLength(60)]}),
        city: new FormControl('', {nonNullable: true, validators: [Validators.maxLength(60)]}),
        rating: new FormControl(5, {nonNullable: true, validators: [Validators.required, Validators.min(1), Validators.max(5)]}),
        text: new FormControl('', {nonNullable: true, validators: [Validators.required, Validators.maxLength(2000)]}),
        phoneNumber: new FormControl('', {nonNullable: true, validators: [Validators.maxLength(40)]}),
        source: new FormControl<ReviewSource>('FACEBOOK', {nonNullable: true}),
        verifiedPurchase: new FormControl(false, {nonNullable: true}),
        approved: new FormControl(true, {nonNullable: true}),
        createdAt: new FormControl('', {nonNullable: true})
    });

    constructor(private readonly reviewService: ReviewService,
                private readonly http: HttpClient) {
    }

    ngOnInit(): void {
        this.load();
        const params = new HttpParams().set('page', '0').set('size', '500').set('sort', 'name,asc');
        this.http.get<Page<Product>>(environment.domain.backend + environment.api.products + '/paged', {params})
            .subscribe(page => this.products = page.content);
    }

    get pendingCount(): number {
        return this.reviews.filter(review => !review.approved).length;
    }

    productName(id: number | null): string {
        if (id == null) {
            return 'Продавницата (општо)';
        }
        return this.products.find(product => product.id === id)?.name ?? `Производ #${id}`;
    }

    load(): void {
        this.reviewService.all().subscribe({
            next: reviews => this.reviews = reviews,
            error: () => this.error = 'Мислењата не се вчитаа.'
        });
    }

    toggleApproved(review: AdminReview): void {
        this.reviewService.update({...review, approved: !review.approved}).subscribe({
            next: () => this.load(),
            error: () => this.error = 'Промената не се зачува.'
        });
    }

    remove(review: AdminReview): void {
        if (!review.id || !confirm(`Да се избрише мислењето од ${review.authorName}?`)) {
            return;
        }
        this.reviewService.delete(review.id).subscribe({
            next: () => this.load(),
            error: () => this.error = 'Мислењето не се избриша.'
        });
    }

    startAdd(): void {
        this.editingId = null;
        this.form.reset();
        this.showForm = true;
    }

    startEdit(review: AdminReview): void {
        this.editingId = review.id ?? null;
        this.form.reset({
            productId: review.productId,
            authorName: review.authorName,
            city: review.city ?? '',
            rating: review.rating,
            text: review.text,
            phoneNumber: review.phoneNumber ?? '',
            source: review.source,
            verifiedPurchase: review.verifiedPurchase,
            approved: review.approved,
            createdAt: review.createdAt ? review.createdAt.substring(0, 10) : ''
        });
        this.showForm = true;
    }

    save(): void {
        if (this.form.invalid || this.saving) {
            this.form.markAllAsTouched();
            return;
        }
        const value = this.form.getRawValue();
        const review: AdminReview = {
            id: this.editingId ?? undefined,
            productId: value.productId == null ? null : Number(value.productId),
            authorName: value.authorName.trim(),
            city: value.city.trim() || null,
            rating: Number(value.rating),
            text: value.text.trim(),
            phoneNumber: value.phoneNumber.trim() || null,
            source: value.source,
            verifiedPurchase: value.verifiedPurchase,
            approved: value.approved,
            // A date from the form is the day the comment was written (e.g. on Facebook).
            createdAt: value.createdAt ? `${value.createdAt}T12:00:00` : null
        };
        this.saving = true;
        const request = this.editingId ? this.reviewService.update(review) : this.reviewService.create(review);
        request.subscribe({
            next: () => {
                this.saving = false;
                this.showForm = false;
                this.error = '';
                this.load();
            },
            error: () => {
                this.saving = false;
                this.error = 'Мислењето не се зачува. Проверете ги полињата.';
            }
        });
    }
}
